import api from './api';
import { Invoice, InvoiceItem } from '../types';

export interface IssuerProfile {
  issuerName: string;
  issuerCompany: string;
  issuerSwiftCode: string;
  issuerIban: string;
  issuerCardNumber: string;
  issuerBankName: string;
  currency: string;
  hourlyRate: number;
}

export interface HoursPreview {
  company: {
    clientName: string;
    clientEmail: string;
    clientCompanyName: string;
  };
  hourlyRate: number;
  currency: string;
  entryCount: number;
  totalHours: number;
  billedHours?: number;
  amount: number;
  items: Array<InvoiceItem & { amount: number }>;
  missingRate: boolean;
  missingRateReason?: string;
  support?: {
    includedHours: number;
    usedHours: number;
    coveredHours: number;
    overageHours: number;
    remainingHours: number;
    overageRate: number;
    periodLabel: string;
  } | null;
  projectsFee?: {
    eligible: boolean;
    included: boolean;
    alreadyBilled: boolean;
    amount: number;
  };
  deals?: {
    included: number;
    skipped: number;
  };
}

export interface CreateInvoicePayload {
  clientCompany?: string;
  clientName: string;
  clientEmail: string;
  clientCompanyName?: string;
  clientAddress?: string;
  clientCity?: string;
  clientState?: string;
  clientCountry?: string;
  clientZipCode?: string;
  clientPhone?: string;
  clientTaxId?: string;
  items: InvoiceItem[];
  amount: number;
  currency?: string;
  notes?: string;
  description?: string;
  dueDate: Date | string;
  reminderEnabled?: boolean;
  reminderHour?: number;
  reminderMinute?: number;
}

export const invoiceService = {
  async getInvoices(search?: string): Promise<Invoice[]> {
    const response = await api.get('/invoices', { params: search ? { search } : undefined });
    return response.data.data;
  },

  async getInvoice(id: string): Promise<Invoice> {
    const response = await api.get(`/invoices/${id}`);
    return response.data.data;
  },

  async getPublicInvoice(id: string): Promise<Invoice> {
    const response = await api.get(`/invoices/public/${id}`);
    return response.data.data;
  },

  async getIssuerProfile(): Promise<IssuerProfile> {
    const response = await api.get('/invoices/issuer');
    return response.data.data;
  },

  async createInvoice(payload: CreateInvoicePayload): Promise<Invoice> {
    const response = await api.post('/invoices', payload);
    return response.data.data;
  },

  async previewHours(companyId: string, from?: string, to?: string): Promise<HoursPreview> {
    const response = await api.get('/invoices/hours-preview', {
      params: { companyId, from, to }
    });
    return response.data.data;
  },

  async createFromHours(payload: {
    companyId: string;
    from?: string;
    to?: string;
    dueDate?: Date | string;
    notes?: string;
    description?: string;
  }): Promise<Invoice> {
    const response = await api.post('/invoices/from-hours', payload);
    return response.data.data;
  },

  async markAsPaid(id: string, paymentMethod = 'Manual'): Promise<Invoice> {
    const response = await api.patch(`/invoices/${id}/pay`, { paymentMethod });
    return response.data.data;
  },

  async sendReminder(id: string): Promise<void> {
    await api.post(`/invoices/${id}/send-reminder`);
  },

  async deleteInvoice(id: string): Promise<void> {
    await api.delete(`/invoices/${id}`);
  }
};
